/**
 * OaSIS Explorer Frontend - Field Shorthand Mapping
 * Task 9.4: Measurement Field Shorthand System
 * 
 * Static mapping of measurement field names to shorthand codes
 * Imported from pre-generated data file for optimal performance
 */

import {
  FIELD_SHORTHAND_MAPPING,
  getFieldShorthand as getShorthand,
  getFieldFullName as getFullName,
  isFieldShorthand as isShorthand,
  isFieldFullName as isFullName,
  getAllFieldShorthands as getAllShorthands,
  getAllShorthandFields as getAllFields
} from '../data/fieldShorthandMapping';

/**
 * Complete field shorthand mapping for all measurement fields
 * Generated deterministically to ensure consistency across environments
 * 
 * Note: This is now imported from a static file for performance.
 * To regenerate: run `node backend/scripts/generate-field-mapping-standalone.js`
 */
export const FIELD_SHORTHAND_MAP = {
  fullToShorthand: FIELD_SHORTHAND_MAPPING.fullToShorthand,
  shorthandToFull: FIELD_SHORTHAND_MAPPING.shorthandToFull,
  
  // Helper methods for backwards compatibility with the runtime-generated version
  getShorthand: (fieldName: string) => getShorthand(fieldName),
  getFullName: (shorthand: string) => getFullName(shorthand),
  isValidShorthand: (code: string) => isShorthand(code),
  isValidFullName: (name: string) => isFullName(name),
};

/**
 * Get shorthand code for a field name
 * 
 * @param fieldName - Full field name
 * @returns Shorthand code or null if not found
 */
export function getFieldShorthand(fieldName: string): string | null {
  return getShorthand(fieldName);
}

/**
 * Get full field name for a shorthand code
 * 
 * @param shorthand - Shorthand code
 * @returns Full field name or null if not found
 */
export function getFieldFullName(shorthand: string): string | null {
  return getFullName(shorthand);
}

/**
 * Check if a string is a valid shorthand code
 * 
 * @param code - String to check
 * @returns True if valid shorthand code
 */
export function isFieldShorthand(code: string): boolean {
  return isShorthand(code);
}

/**
 * Check if a string is a valid full field name
 * 
 * @param name - String to check
 * @returns True if valid full field name
 */
export function isFieldFullName(name: string): boolean {
  return isFullName(name);
}

/**
 * Get all field name → shorthand mappings
 * 
 * @returns Object with all field name to shorthand mappings
 */
export function getAllFieldShorthands(): Record<string, string> {
  return getAllShorthands();
}

/**
 * Get all shorthand → field name mappings
 * 
 * @returns Object with all shorthand to field name mappings
 */
export function getAllShorthandFields(): Record<string, string> {
  return getAllFields();
}

/**
 * Validate the field shorthand mapping
 * 
 * @returns Validation result with mapping statistics
 */
export function validateFieldShorthandMap() {
  const totalFields = Object.keys(FIELD_SHORTHAND_MAPPING.fullToShorthand).length;
  return {
    ...FIELD_SHORTHAND_MAP,
    totalFields,
    // Keep this tied to the generated map size rather than a stale hardcoded value.
    isValid: totalFields > 0,
  };
}

// Export the mapping for direct access
export { FIELD_SHORTHAND_MAP as FIELD_SHORTHAND_MAP_EXPORT };
